import { CheckCircleOutlined, EnvironmentOutlined, FileTextOutlined, ToolOutlined } from '@ant-design/icons';
import { api } from '@common';
import { InputMask, PrimaryButton } from '@components/atoms';
import { Checkbox, Col, DatePicker, Form, Input, message, Row, Select, Steps, Typography } from 'antd';
import enUS from 'antd/lib/locale/en_US';
import dayjs from 'dayjs';
import 'dayjs/locale/en';
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './styles.css';

dayjs.locale('en');

const { Title, Paragraph } = Typography;
const { TextArea } = Input;
const { Option } = Select;
const { Step } = Steps;

interface QuoteFormValues {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  projectType: string;
  projectDescription: string;
  projectAddress: string;
  projectCity: string;
  projectState: string;
  projectZip: string;
  preferredStartDate: Date;
  budget: string;
  howDidYouHear: string;
  termsAgreed: boolean;
}

export const QuotePage = () => {
  const navigate = useNavigate();
  const [current, setCurrent] = useState(0);
  const [formData, setFormData] = useState<Partial<QuoteFormValues>>({});
  const formRef = useRef<HTMLDivElement>(null);

  const handleNext = (values: Partial<QuoteFormValues>) => {
    setFormData({ ...formData, ...values });
    setCurrent(current + 1);
    setTimeout(() => {
      if (formRef.current) {
        formRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  };

  const handlePrev = () => {
    setCurrent(current - 1);
    setTimeout(() => {
      if (formRef.current) {
        formRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 200);
  };

  const handleSubmit = async (values: Partial<QuoteFormValues>) => {
    const finalData = { ...formData, ...values };

    try {
      await api.post('/contact/quote', finalData);
      message.success('Your quote request has been submitted successfully!');
      setCurrent(current + 1);
      setTimeout(() => {
        if (formRef.current) {
          formRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 200);
    } catch (error) {
      message.error('Unable to submit your quote request. Please try again later.');
    }
  };

  return (
    <div>
      {/* Hero Section */}
      <section className="bg-zinc-900 w-full relative h-[400px]">
        <div className="container relative h-full flex flex-col justify-center items-center mx-auto px-4 py-12 md:py-16 lg:py-20 z-10">
          <Title level={1} className="font-android !font-normal text-yellow-principal text-center">
            GET A QUOTE
          </Title>
          <div className="max-w-4xl mx-auto">
            <Paragraph className="font-barlow text-gray-300 text-center md:text-lg lg:!text-xl">
              Fill out the form below to request a quote for your project. Our team will review your requirements and get back to you within 1-2 business days with a detailed estimate.
            </Paragraph>
          </div>
        </div>
      </section>

      {/* Quote Form Section */}
      <section className="w-full py-18 bg-second-black-carbon">
        <div className="container mx-auto px-4" ref={formRef}>
          <div className="max-w-5xl mx-auto">
            <div className="steps-container mb-8">
              <Steps current={current} className="font-android !font-normal quote-steps">
                <Step title="PROJECT INFORMATION" icon={<div className="step-icon-wrapper"><FileTextOutlined /></div>} />
                <Step title="PROJECT LOCATION" icon={<div className="step-icon-wrapper"><EnvironmentOutlined /></div>} />
                <Step title="ADDITIONAL DETAILS" icon={<div className="step-icon-wrapper"><ToolOutlined /></div>} />
                <Step title="COMPLETE" icon={<div className="step-icon-wrapper"><CheckCircleOutlined /></div>} />
              </Steps>
            </div>

            {current === 0 && (
              <div>
                <Title level={2} className="font-android !font-normal !text-white !mb-8">STEP 1: PROJECT INFORMATION</Title>

                <Form
                  name="quote-form-1"
                  layout="vertical"
                  onFinish={handleNext}
                  requiredMark={false}
                  initialValues={formData}
                >
                  <Row gutter={16}>
                    <Col xs={24} md={12}>
                      <Form.Item
                        label={<span className="font-android !font-normal text-white">FIRST NAME</span>}
                        name="firstName"
                        rules={[{ required: true, message: 'Please enter your first name' }]}
                      >
                        <Input className="bg-zinc-800 font-barlow !text-base" />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                      <Form.Item
                        label={<span className="font-android !font-normal text-white">LAST NAME</span>}
                        name="lastName"
                        rules={[{ required: true, message: 'Please enter your last name' }]}
                      >
                        <Input className="bg-zinc-800 font-barlow !text-base" />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={16}>
                    <Col xs={24} md={12}>
                      <Form.Item
                        label={<span className="font-android !font-normal text-white">EMAIL ADDRESS</span>}
                        name="email"
                        rules={[
                          { required: true, message: 'Please enter your email' },
                          { type: 'email', message: 'Please enter a valid email' }
                        ]}
                      >
                        <Input className="bg-zinc-800 font-barlow !text-base" />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                      <Form.Item
                        label={<span className="font-android !font-normal text-white">PHONE NUMBER</span>}
                        name="phone"
                        rules={[{ required: true, message: 'Please enter your phone number' }]}
                      >
                        <InputMask mask="usphone" className="bg-zinc-800 font-barlow !text-base" />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Form.Item
                    label={<span className="font-android !font-normal text-white">PROJECT TYPE</span>}
                    name="projectType"
                    rules={[{ required: true, message: 'Please select a project type' }]}
                  >
                    <Select className="bg-zinc-800" popupClassName="bg-zinc-800">
                      <Option value="excavation">Excavation</Option>
                      <Option value="site-preparation">Site Preparation</Option>
                      <Option value="mastication">Mastication</Option>
                      <Option value="snow-removal">Commercial Snow Removal</Option>
                      <Option value="remodel">Interior/Exterior Remodel</Option>
                      <Option value="steel-fabrication">Steel Fabrication</Option>
                      <Option value="other">Other (please specify)</Option>
                    </Select>
                  </Form.Item>

                  <Form.Item
                    label={<span className="font-android !font-normal text-white">PROJECT DESCRIPTION</span>}
                    name="projectDescription"
                    rules={[{ required: true, message: 'Please describe your project' }]}
                  >
                    <TextArea
                      rows={6}
                      className="bg-zinc-800 font-barlow !text-base"
                      placeholder="Please provide details about your project, including scope, timeline, and any specific requirements."
                    />
                  </Form.Item>

                  <Form.Item className="w-fit">
                    <PrimaryButton
                      type="primary"
                      htmlType="submit"
                      size="large"
                    >
                      NEXT STEP
                    </PrimaryButton>
                  </Form.Item>
                </Form>
              </div>
            )}

            {current === 1 && (
              <div>
                <Title level={2} className="font-android !font-normal !text-white mb-8">STEP 2: PROJECT LOCATION</Title>

                <Form
                  name="quote-form-2"
                  layout="vertical"
                  onFinish={handleNext}
                  requiredMark={false}
                  initialValues={formData}
                >
                  <Form.Item
                    label={<span className="font-android !font-normal text-white">PROJECT ADDRESS</span>}
                    name="projectAddress"
                    rules={[{ required: true, message: 'Please enter the project address' }]}
                  >
                    <Input className="bg-zinc-800 font-barlow !text-base" />
                  </Form.Item>

                  <Row gutter={16}>
                    <Col xs={24} md={12}>
                      <Form.Item
                        label={<span className="font-android !font-normal text-white">CITY</span>}
                        name="projectCity"
                        rules={[{ required: true, message: 'Please enter the city' }]}
                      >
                        <Input className="bg-zinc-800 font-barlow !text-base" />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={6}>
                      <Form.Item
                        label={<span className="font-android !font-normal text-white">STATE</span>}
                        name="projectState"
                        rules={[{ required: true, message: 'Please enter the state' }]}
                      >
                        <Input className="bg-zinc-800 font-barlow !text-base" />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={6}>
                      <Form.Item
                        label={<span className="font-android !font-normal text-white">ZIP CODE</span>}
                        name="projectZip"
                        rules={[{ required: true, message: 'Please enter the zip code' }]}
                      >
                        <InputMask mask="zipCode" className="bg-zinc-800 font-barlow !text-base" />
                      </Form.Item>
                    </Col>
                  </Row>

                  <div className="flex justify-between mt-8">
                    <PrimaryButton
                      onClick={handlePrev}
                      size="large"
                    >
                      PREVIOUS STEP
                    </PrimaryButton>
                    <PrimaryButton
                      type="primary"
                      htmlType="submit"
                      size="large"
                    >
                      NEXT STEP
                    </PrimaryButton>
                  </div>
                </Form>
              </div>
            )}

            {current === 2 && (
              <div>
                <Title level={2} className="font-android !font-normal !text-white my-8">STEP 3: ADDITIONAL DETAILS</Title>

                <Form
                  name="quote-form-3"
                  layout="vertical"
                  onFinish={handleSubmit}
                  requiredMark={false}
                  initialValues={formData}
                >
                  <Form.Item
                    label={<span className="font-android !font-normal text-white">PREFERRED START DATE</span>}
                    name="preferredStartDate"
                  >
                    <DatePicker
                      className="w-full bg-zinc-800"
                      format="MM/DD/YYYY"
                      placeholder="Select a date"
                      locale={enUS.DatePicker}
                    />
                  </Form.Item>

                  <Form.Item
                    label={<span className="font-android !font-normal text-white">BUDGET RANGE</span>}
                    name="budget"
                  >
                    <Select className="bg-zinc-800" popupClassName="bg-zinc-800">
                      <Option value="under-5k">Under $5,000</Option>
                      <Option value="5k-15k">$5,000 - $15,000</Option>
                      <Option value="15k-30k">$15,000 - $30,000</Option>
                      <Option value="30k-50k">$30,000 - $50,000</Option>
                      <Option value="50k-100k">$50,000 - $100,000</Option>
                      <Option value="over-100k">Over $100,000</Option>
                      <Option value="not-sure">Not sure yet</Option>
                    </Select>
                  </Form.Item>

                  <Form.Item
                    label={<span className="font-android !font-normal text-white">HOW DID YOU HEAR ABOUT US?</span>}
                    name="howDidYouHear"
                  >
                    <Select className="bg-zinc-800" popupClassName="bg-zinc-800">
                      <Option value="search">Online Search</Option>
                      <Option value="social">Social Media</Option>
                      <Option value="referral">Referral</Option>
                      <Option value="repeat">Previous Customer</Option>
                      <Option value="advertisement">Advertisement</Option>
                      <Option value="other">Other</Option>
                    </Select>
                  </Form.Item>

                  <Form.Item
                    name="termsAgreed"
                    valuePropName="checked"
                    rules={[
                      { required: true, message: 'You must agree to the terms' }
                    ]}
                  >
                    <Checkbox>
                      I agree to Ruppert's terms of service and privacy policy
                    </Checkbox>
                  </Form.Item>

                  <div className="flex justify-between mt-8">
                    <PrimaryButton
                      onClick={handlePrev}
                      size="large"
                    >
                      PREVIOUS STEP
                    </PrimaryButton>
                    <PrimaryButton
                      type="primary"
                      htmlType="submit"
                      size="large"
                    >
                      SUBMIT QUOTE REQUEST
                    </PrimaryButton>
                  </div>
                </Form>
              </div>
            )}

            {current === 3 && (
              <div className="text-center py-12">
                <CheckCircleOutlined className="text-yellow-principal text-8xl mb-16" />
                <Title level={2} className="font-android !font-normal !text-white mb-4">QUOTE REQUEST SUBMITTED!</Title>
                <Paragraph className="font-barlow text-gray-300 text-lg !mb-12">
                  Thank you for submitting your quote request. Our team will review your project details and get back to you within 1-2 business days with a detailed estimate.
                </Paragraph>
                <div className="flex justify-center gap-8">
                  <PrimaryButton
                    type="primary"
                    onClick={() => {
                      navigate('/');
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    size="large"
                  >
                    RETURN TO HOMEPAGE
                  </PrimaryButton>
                  <PrimaryButton
                    onClick={() => {
                      navigate('/contact');
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    size="large"
                  >
                    CONTACT US
                  </PrimaryButton>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="w-full py-16 bg-zinc-900 flex justify-center">
        <div className="container mx-auto px-4 flex flex-col items-center">
          <Title level={2} className="font-android !font-normal text-yellow-principal text-center mb-16 pb-6">OUR QUOTE PROCESS</Title>

          <Row gutter={[64, 48]} justify="center" className="w-full">
            <Col xs={24} sm={20} md={7} lg={7} xl={7}>
              <div className="text-center">
                <div className="inline-flex bg-yellow-principal text-2xl font-bold w-12 h-12 items-center justify-center mb-6">
                  <span className="font-android !font-normal text-black !text-3xl flex items-center justify-center h-full">1</span>
                </div>
                <Title level={3} className="font-android !font-normal !text-white mb-4 text-lg">SUBMIT REQUEST</Title>
                <Paragraph className="font-barlow text-gray-300 text-center">
                  Fill out our quote request form with details about your project needs and requirements.
                </Paragraph>
              </div>
            </Col>

            <Col xs={24} sm={20} md={7} lg={7} xl={7}>
              <div className="text-center">
                <div className="inline-flex bg-yellow-principal text-2xl font-bold w-12 h-12 items-center justify-center mb-6">
                  <span className="font-android !font-normal text-black !text-3xl flex items-center justify-center h-full">2</span>
                </div>
                <Title level={3} className="font-android !font-normal !text-white mb-4 text-lg">PROJECT ASSESSMENT</Title>
                <Paragraph className="font-barlow text-gray-300 text-center">
                  Our team reviews your request and may schedule a site visit to better understand the scope.
                </Paragraph>
              </div>
            </Col>

            <Col xs={24} sm={20} md={7} lg={7} xl={7}>
              <div className="text-center">
                <div className="inline-flex bg-yellow-principal text-2xl font-bold w-12 h-12 items-center justify-center mb-6">
                  <span className="font-android !font-normal text-black !text-3xl flex items-center justify-center h-full">3</span>
                </div>
                <Title level={3} className="font-android !font-normal !text-white mb-4 text-lg">DETAILED ESTIMATE</Title>
                <Paragraph className="font-barlow text-gray-300 text-center">
                  We provide you with a comprehensive quote including timeline, materials, and total cost.
                </Paragraph>
              </div>
            </Col>
          </Row>
        </div>
      </section>

      {/* FAQ Section */}
      {/* <section className="w-full py-20 bg-black">
        <div className="container mx-auto px-4">
          <Title level={2} className="font-android !font-normal !text-white text-center mb-16">FREQUENTLY ASKED QUESTIONS</Title>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <div className="bg-zinc-900 p-6 rounded-lg">
              <Title level={4} className="font-android !font-normal !text-white mb-4">HOW LONG DOES THE QUOTE PROCESS TAKE?</Title>
              <Paragraph className="font-barlow text-gray-300">
                In most cases, we can provide a detailed quote within 1-2 business days. More complex projects may require a site visit and additional time.
              </Paragraph>
            </div>

            <div className="bg-zinc-900 p-6 rounded-lg">
              <Title level={4} className="font-android !font-normal !text-white mb-4">IS THERE A FEE FOR GETTING A QUOTE?</Title>
              <Paragraph className="font-barlow text-gray-300">
                No, our quotes are completely free with no obligation. We believe in transparency and want you to have all the information you need to make an informed decision.
              </Paragraph>
            </div>

            <div className="bg-zinc-900 p-6 rounded-lg">
              <Title level={4} className="font-android !font-normal !text-white mb-4">WHAT INFORMATION DO I NEED TO PROVIDE?</Title>
              <Paragraph className="font-barlow text-gray-300">
                The more details you can provide about your project, the more accurate your quote will be. Project scope, timeline, location, and any specific requirements are all helpful.
              </Paragraph>
            </div>

            <div className="bg-zinc-900 p-6 rounded-lg">
              <Title level={4} className="font-android !font-normal !text-white mb-4">HOW LONG IS MY QUOTE VALID?</Title>
              <Paragraph className="font-barlow text-gray-300">
                Our quotes are typically valid for 30 days from the date of issue. After that, material prices and availability may change, requiring an updated quote.
              </Paragraph>
            </div>
          </div>
        </div>
      </section> */}
    </div>
  );
};